import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  MarkEmailRead as MarkReadIcon,
  CheckCircle as ResolveIcon,
  Delete as DismissIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';

const AdminNotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        const isUserAdmin = user?.app_metadata?.app_role === 'admin' || user?.raw_app_meta_data?.app_role === 'admin';
        setIsAdmin(isUserAdmin);
        
        if (!isUserAdmin) {
          setError('You do not have permission to access admin notifications.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    checkAdmin();
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get total count for pagination
      const countQuery = supabase
        .from('admin_notifications')
        .select('id', { count: 'exact', head: true });
      
      if (statusFilter) countQuery.eq('status', statusFilter);
      if (typeFilter) countQuery.eq('notification_type', typeFilter);
      if (severityFilter) countQuery.eq('severity', severityFilter);
      
      const { count, error: countError } = await countQuery;
      if (countError) throw countError;
      setTotalCount(count || 0);

      // Get notifications
      const { data, error: fetchError } = await supabase.rpc('get_admin_notifications', {
        p_limit: rowsPerPage,
        p_offset: page * rowsPerPage,
        p_status: statusFilter || null,
        p_notification_type: typeFilter || null,
        p_severity: severityFilter || null,
      });
      
      if (fetchError) throw fetchError;
      setNotifications(data || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, rowsPerPage, statusFilter, typeFilter, severityFilter]);

  useEffect(() => {
    if (isAdmin) {
      fetchNotifications();
    }
  }, [isAdmin, fetchNotifications]);

  // Action handlers
  const markAsRead = async (id) => {
    try {
      await supabase.rpc('mark_notification_read', { p_notification_id: id });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const resolveNotification = async (id) => {
    try {
      await supabase.rpc('resolve_notification', { p_notification_id: id });
      fetchNotifications();
    } catch (err) {
      console.error('Error resolving notification:', err);
    }
  };

  const dismissNotification = async (id) => {
    try {
      await supabase.rpc('dismiss_notification', { p_notification_id: id });
      fetchNotifications();
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await supabase.rpc('mark_all_notifications_read');
      fetchNotifications();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  // Helper functions
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <ErrorIcon sx={{ color: '#d32f2f' }} />;
      case 'high': return <WarningIcon sx={{ color: '#ed6c02' }} />;
      case 'medium': return <InfoIcon sx={{ color: '#0288d1' }} />;
      default: return <InfoIcon sx={{ color: '#2e7d32' }} />;
    }
  };

  const getSeverityChip = (severity) => {
    const colors = {
      critical: { bg: '#ffebee', text: '#d32f2f' },
      high: { bg: '#fff3e0', text: '#ed6c02' },
      medium: { bg: '#e3f2fd', text: '#0288d1' },
      low: { bg: '#e8f5e9', text: '#2e7d32' },
    };
    const c = colors[severity] || colors.low;
    return (
      <Chip 
        label={severity} 
        size="small" 
        sx={{ bgcolor: c.bg, color: c.text, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }} 
      />
    );
  };

  const getStatusChip = (status) => {
    const colors = {
      unread: { bg: '#e3f2fd', text: '#1565c0' },
      read: { bg: '#f5f5f5', text: '#666' },
      acknowledged: { bg: '#fff8e1', text: '#f57c00' },
      resolved: { bg: '#e8f5e9', text: '#2e7d32' },
      dismissed: { bg: '#fafafa', text: '#9e9e9e' },
    };
    const c = colors[status] || colors.read;
    return (
      <Chip 
        label={status} 
        size="small" 
        sx={{ bgcolor: c.bg, color: c.text, fontWeight: 500, textTransform: 'capitalize' }} 
      />
    );
  };

  const getTypeLabel = (type) => {
    const labels = {
      'limit_error': '📊 Limit Error',
      'wordpress_error': '🔌 WordPress',
      'api_error': '🔗 API Error',
      'payment_error': '💳 Payment',
      'system_error': '⚙️ System',
      'security_alert': '🔒 Security',
      'user_feedback': '💬 Feedback',
      'integration_error': '🔧 Integration',
      'crawl_error': '🕷️ Crawl',
      'embedding_error': '🧠 Embedding',
      'other': '📌 Other',
    };
    return labels[type] || type;
  };

  if (!isAdmin) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 4 }}>
          {error || 'You do not have permission to access admin notifications.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        pt: 4,
        pb: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              component={RouterLink}
              to="/admin"
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
              Admin Notifications
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<MarkReadIcon />}
              onClick={markAllAsRead}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                textTransform: 'none',
              }}
            >
              Mark All Read
            </Button>
            <IconButton
              onClick={fetchNotifications}
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
            >
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Filters */}
        <Paper sx={{ mb: 3, p: 2, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <FilterIcon sx={{ color: '#666' }} />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="unread">Unread</MenuItem>
                <MenuItem value="read">Read</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="dismissed">Dismissed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="limit_error">Limit Error</MenuItem>
                <MenuItem value="wordpress_error">WordPress</MenuItem>
                <MenuItem value="api_error">API Error</MenuItem>
                <MenuItem value="payment_error">Payment</MenuItem>
                <MenuItem value="system_error">System</MenuItem>
                <MenuItem value="security_alert">Security</MenuItem>
                <MenuItem value="crawl_error">Crawl</MenuItem>
                <MenuItem value="embedding_error">Embedding</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Severity</InputLabel>
              <Select
                value={severityFilter}
                label="Severity"
                onChange={(e) => { setSeverityFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <Chip 
              label={`${totalCount} total`} 
              variant="outlined" 
              size="small" 
            />
          </Stack>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Notifications Table */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center', color: '#888' }}>
              <InfoIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6">No notifications found</Typography>
              <Typography variant="body2">Try adjusting your filters</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 140 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>Severity</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 150 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 160 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 140 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifications.map((notif) => (
                      <TableRow 
                        key={notif.id} 
                        sx={{ 
                          bgcolor: notif.status === 'unread' ? '#f8f9ff' : 'inherit',
                          '&:hover': { bgcolor: '#f5f5f5' },
                        }}
                      >
                        <TableCell>{getSeverityIcon(notif.severity)}</TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: notif.status === 'unread' ? 600 : 400 }}>
                            {notif.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem' }}>
                            {notif.message.length > 100 ? `${notif.message.substring(0, 100)}...` : notif.message}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getTypeLabel(notif.notification_type)} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>{getSeverityChip(notif.severity)}</TableCell>
                        <TableCell>{getStatusChip(notif.status)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {notif.user_email || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {new Date(notif.created_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {notif.status === 'unread' && (
                              <Tooltip title="Mark as read">
                                <IconButton size="small" onClick={() => markAsRead(notif.id)}>
                                  <MarkReadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {notif.status !== 'resolved' && (
                              <Tooltip title="Resolve">
                                <IconButton size="small" onClick={() => resolveNotification(notif.id)} color="success">
                                  <ResolveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {notif.status !== 'dismissed' && (
                              <Tooltip title="Dismiss">
                                <IconButton size="small" onClick={() => dismissNotification(notif.id)} color="default">
                                  <DismissIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminNotificationsPage;


